import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const sectorProjects = [
  { sector: "Health", color: "bg-emerald-500" },
  { sector: "Education", color: "bg-amber-500" },
  { sector: "Economic Security", color: "bg-teal-500" },
  { sector: "Protection", color: "bg-rose-500" },
];

interface MonthlyData {
  month: string;
  articles: number;
  applications: number;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Published: "bg-emerald-100 text-emerald-700",
    Draft: "bg-stone-100 text-stone-500",
    New: "bg-amber-100 text-amber-700",
    Reviewed: "bg-teal-100 text-teal-700",
    Accepted: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] ?? "bg-stone-100 text-stone-500"}`}>
      {status}
    </span>
  );
}

function buildMonthlyData(articles: any[], applications: any[]): MonthlyData[] {
  const months: MonthlyData[] = [];
  const today = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const label = d.toLocaleDateString("en-US", { month: "short" });
    const y = d.getFullYear();
    const m = d.getMonth();
    const artCount = articles.filter((a) => {
      const ad = new Date(a.date || a.created_at);
      return ad.getFullYear() === y && ad.getMonth() === m;
    }).length;
    const appCount = applications.filter((a) => {
      const ad = new Date(a.date || a.created_at);
      return ad.getFullYear() === y && ad.getMonth() === m;
    }).length;
    months.push({ month: label, articles: artCount, applications: appCount });
  }
  return months;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("This Month");
  const [articles, setArticles] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    const promises = [
      supabase.from("articles").select("*").order("date", { ascending: false }),
      supabase.from("projects").select("*"),
      supabase.from("staff_members").select("*"),
      supabase.from("applications").select("*").order("date", { ascending: false }),
    ];
    const results = await Promise.all(promises);

    if (!results[0].error) setArticles(results[0].data || []);
    if (!results[1].error) setProjects(results[1].data || []);
    if (!results[2].error) setStaff(results[2].data || []);
    if (!results[3].error) setApplications(results[3].data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const monthlyData = buildMonthlyData(articles, applications);
  const maxValue = Math.max(
    ...monthlyData.map((d) => Math.max(d.articles, d.applications)),
    1
  );

  const getProjectPct = (sector: string) => {
    if (projects.length === 0) return 0;
    return Math.round((projects.filter((p) => p.sector === sector).length / projects.length) * 100);
  };

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const activeArticles = articles.filter((a) => a.status === "Published").length;
  const activeProjects = projects.filter((p) => p.status === "Active").length;
  const activeStaff = staff.filter((s) => s.status === "Active").length;
  const totalApplications = applications.length;

  // "this month" changes for the stat cards
  const thisMonth = new Date();
  const thisMonthApps = applications.filter((a) => {
    const d = new Date(a.date || a.created_at);
    return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
  }).length;
  const thisMonthArticles = articles.filter((a) => {
    const d = new Date(a.date || a.created_at);
    return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
  }).length;

  const stats = [
    { icon: "ri-article-line", label: "Total Articles", value: activeArticles.toString(), change: `+${thisMonthArticles} this month`, color: "bg-amber-50 text-amber-600", trend: "up" as const },
    { icon: "ri-folder-chart-line", label: "Active Projects", value: activeProjects.toString(), change: "+2 this month", color: "bg-emerald-50 text-emerald-600", trend: "up" as const },
    { icon: "ri-team-line", label: "Staff Members", value: activeStaff.toString(), change: "No change", color: "bg-teal-50 text-teal-600", trend: "neutral" as const },
    { icon: "ri-heart-line", label: "Applications", value: totalApplications.toString(), change: `+${thisMonthApps} this month`, color: "bg-rose-50 text-rose-600", trend: "up" as const },
  ];

  const recentArticles = articles.slice(0, 4);
  const recentApplications = applications.slice(0, 4);

  const subtitle = user ? `Welcome back, ${user.name.split(" ")[0]}. Here's what's happening.` : "Welcome back. Here's what's happening.";

  return (
    <AdminLayout title="Dashboard" subtitle={subtitle}>
      {/* Period selector */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone-500">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
        <div className="flex items-center bg-white border border-stone-200 rounded-xl overflow-hidden">
          {["This Week", "This Month", "This Year"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                period === p ? "bg-primary text-white" : "text-stone-500 hover:bg-stone-50"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}

      {!loading && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.color}`}>
                    <i className={`${s.icon} text-lg`}></i>
                  </div>
                  {s.trend === "up" && (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                      <i className="ri-arrow-up-line"></i>
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-stone-800 mb-1">{s.value}</p>
                <p className="text-xs text-stone-500 font-medium">{s.label}</p>
                <p className="text-xs text-stone-400 mt-1">{s.change}</p>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Activity chart */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-stone-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-sm font-bold text-stone-800">Activity Overview</h3>
                  <p className="text-xs text-stone-400 mt-0.5">Articles published &amp; applications received — last 6 months</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-stone-500">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary inline-block"></span>Applications</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>Articles</span>
                </div>
              </div>
              <div className="flex items-end gap-3 h-40">
                {monthlyData.map((d) => {
                  const appPct = Math.max((d.applications / maxValue) * 100, 2);
                  const artPct = Math.max((d.articles / maxValue) * 100, 2);
                  return (
                    <div key={d.month} className="flex-1 flex flex-col items-center gap-2 group">
                      <div className="relative w-full flex justify-center items-end gap-1 h-28">
                        {/* Applications bar */}
                        <div className="w-3 md:w-4 bg-primary/90 rounded-t-md transition-all duration-300 group-hover:bg-primary relative" style={{ height: `${appPct}%` }}>
                          {d.applications > 0 && (
                            <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {d.applications}
                            </span>
                          )}
                        </div>
                        {/* Articles bar */}
                        <div className="w-3 md:w-4 bg-amber-400/90 rounded-t-md transition-all duration-300 group-hover:bg-amber-400 relative" style={{ height: `${artPct}%` }}>
                          {d.articles > 0 && (
                            <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {d.articles}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-stone-400 font-medium">{d.month}</span>
                    </div>
                  );
                })}
              </div>
              {monthlyData.every((d) => d.articles === 0 && d.applications === 0) && (
                <p className="text-center text-xs text-stone-400 mt-4">No activity recorded in the last 6 months</p>
              )}
            </div>

            {/* Projects by sector */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <h3 className="text-sm font-bold text-stone-800 mb-1">Projects by Sector</h3>
              <p className="text-xs text-stone-400 mb-5">{projects.length} projects total</p>
              <div className="flex flex-col gap-4">
                {sectorProjects.map((s) => (
                  <div key={s.sector}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-stone-700">{s.sector}</span>
                      <span className="text-xs text-stone-400">{projects.filter((p) => p.sector === s.sector).length} projects</span>
                    </div>
                    <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className={`h-full ${s.color} rounded-full`} style={{ width: `${getProjectPct(s.sector)}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tables row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Articles */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
                <h3 className="text-sm font-bold text-stone-800">Recent Articles</h3>
                <Link to="/admin/articles" className="text-xs text-primary font-semibold hover:underline cursor-pointer">
                  View all
                </Link>
              </div>
              <div className="divide-y divide-stone-50">
                {recentArticles.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 px-6 py-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg shrink-0 mt-0.5">
                      <i className="ri-article-line text-amber-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-stone-800 truncate">{a.title}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{a.author} &middot; {formatDate(a.date)}</p>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
                {recentArticles.length === 0 && (
                  <div className="px-6 py-6 text-center text-stone-400 text-sm">No articles yet</div>
                )}
              </div>
            </div>

            {/* Recent Applications */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
                <h3 className="text-sm font-bold text-stone-800">Recent Applications</h3>
                <Link to="/admin/interns-volunteers" className="text-xs text-primary font-semibold hover:underline cursor-pointer">
                  View all
                </Link>
              </div>
              <div className="divide-y divide-stone-50">
                {recentApplications.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 px-6 py-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-full shrink-0">
                      <span className="text-xs font-bold text-stone-600">
                        {a.name.split(" ").map((n: string) => n[0]).join("")}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-stone-800">{a.name}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{a.type} &middot; {a.area}</p>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
                {recentApplications.length === 0 && (
                  <div className="px-6 py-6 text-center text-stone-400 text-sm">No applications yet</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}